import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, X, Check, ArrowRight, Images,
  Sparkles, UtensilsCrossed, Waves, Dumbbell, Building2, ShoppingBag, Wifi,
  Clock, Calendar, Tag, Phone, Mail, MessageCircle,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { HOTELS } from "@/lib/data";
import { getHotelDetails } from "@/lib/hotelDetails";
import {
  useHotelPropertyGalleries, getHeroUrl, getGalleryUrls,
} from "@/lib/useHotelPropertyGalleries";
import BrandMonogramEdge from "@/components/BrandMonogramEdge";

const ICONS = {
  Sparkles, UtensilsCrossed, Waves, Dumbbell, Building2, ShoppingBag, Wifi,
};

const pick = (obj, lang) => {
  if (!obj) return "";
  if (typeof obj === "string") return obj;
  return obj[lang] || obj.en || obj.fr || "";
};

const Lightbox = ({ images, title, startIdx = 0, onClose }) => {
  // Sync the visible image with the tile the user clicked. Clamped so
  // out-of-bounds start indices don't fail silently.
  const initial = Math.min(Math.max(parseInt(startIdx, 10) || 0, 0), Math.max(images.length - 1, 0));
  const [idx, setIdx] = useState(initial);
  React.useEffect(() => {
    setIdx(initial);
  }, [initial]);
  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + images.length) % images.length);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [images.length, onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#1A1513]/95 flex items-center justify-center px-4 md:px-12"
      data-testid="service-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-[#FDFBF7]/80 hover:text-[#FDFBF7] p-2"
        data-testid="service-lightbox-close"
      >
        <X className="w-6 h-6" strokeWidth={1.5} />
      </button>
      <button
        onClick={() => setIdx((i) => (i - 1 + images.length) % images.length)}
        className="absolute left-2 md:left-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3"
        data-testid="service-lightbox-prev"
      >
        <ChevronLeft className="w-8 h-8" strokeWidth={1.5} />
      </button>
      <button
        onClick={() => setIdx((i) => (i + 1) % images.length)}
        className="absolute right-2 md:right-8 top-1/2 -translate-y-1/2 text-[#FDFBF7]/70 hover:text-[#FDFBF7] p-3"
        data-testid="service-lightbox-next"
      >
        <ChevronRight className="w-8 h-8" strokeWidth={1.5} />
      </button>
      <div className="relative max-w-6xl w-full">
        <img
          src={images[idx]}
          alt={`${title} ${idx + 1}`}
          className="w-full max-h-[80vh] object-contain" loading="lazy" decoding="async"/>
        <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[11px] tracking-[0.3em] uppercase text-[#FDFBF7]/70">
          {title} · {idx + 1} / {images.length}
        </p>
      </div>
    </div>
  );
};

const ServiceDetailPage = () => {
  const { slug, serviceSlug } = useParams();
  const navigate = useNavigate();
  const { lang } = useLanguage();
  // Lightbox state carries the START INDEX so the modal opens exactly on
  // the tile the user clicked (previously it always opened on the first
  // image, and — because AutoImageEditor could silently rewrite the
  // thumbnail `<img src>` via `image_slots` — the modal often surfaced a
  // completely different image than the one displayed on the page).
  const [lightbox, setLightbox] = useState({ open: false, startIdx: 0 });

  const hotel = HOTELS.find((h) => h.slug === slug);
  const propertyGalleries = useHotelPropertyGalleries(slug);
  if (!hotel) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-sm text-[#5C5248]">Hotel not found.</p>
      </div>
    );
  }
  const d = getHotelDetails(slug);
  const service = (d.services || []).find((s) => s.slug === serviceSlug);

  if (!service) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4">
        <p className="font-serif-x text-3xl">Service not found.</p>
        <button
          onClick={() => navigate(`/hotels/${slug}`)}
          className="text-[11px] tracking-[0.25em] uppercase border-b border-[#2C2621]/40 pb-1"
        >
          ← Back to hotel
        </button>
      </div>
    );
  }

  const Icon = ICONS[service.icon] || Sparkles;

  // Admin-managed property gallery for THIS service (independent of any
  // static `service.image` / `service.gallery` defaults).
  const adminHero = getHeroUrl(propertyGalleries, serviceSlug);
  const adminGalleryUrls = getGalleryUrls(propertyGalleries, serviceSlug);

  // Hero image priority: admin hero → static service.image
  const heroImage = adminHero || service.image;

  // Bottom gallery strip — keep the 8-tile guarantee. When admin has
  // uploaded a gallery we start with those URLs, then pad from the static
  // pool to ensure visual fullness. Otherwise we fall back to the legacy
  // pool entirely.
  const fallbackPool = [
    service.image,
    ...(service.gallery || []),
    ...(hotel.gallery || []),
    hotel.image,
    ...((d.services || []).filter((s) => s.slug && s.slug !== serviceSlug).map((s) => s.image)),
    ...((d.rooms || []).flatMap((r) => [r.image, ...(r.gallery || [])])),
    ...((d.excursions || []).map((e) => e.image)),
  ].filter(Boolean);
  const galleryPool = adminGalleryUrls.length
    ? [...adminGalleryUrls, ...fallbackPool]
    : fallbackPool;
  const gallery = Array.from(new Set(galleryPool));

  const otherServices = (d.services || []).filter((s) => s.slug && s.slug !== serviceSlug).slice(0, 4);

  const CTA_LABELS = {
    request: { en: "Request this service", fr: "Demander ce service", es: "Solicitar este servicio" },
    book: { en: "Book now", fr: "Réserver", es: "Reservar" },
  };

  return (
    <div data-testid="page-service-detail">
      {/* HERO */}
      <section className="relative min-h-[70vh] flex flex-col justify-end" data-testid="service-detail-hero" data-cms-scope={`service.${slug}.${serviceSlug}`}>
        <img
          src={heroImage}
          alt={pick(service.title, lang)}
          className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async"
          data-cms-role="hero"/>
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/45 to-[#1A1513]/15" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 flex flex-col pb-14 text-[#FDFBF7]">
          <Link
            to={`/hotels/${slug}`}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#FDFBF7]/85 hover:text-[#D4A373] mb-6"
            data-testid="back-to-hotel"
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
            {pick({ en: "Back to", fr: "Retour à", es: "Volver a" }, lang)} {hotel.name}
          </Link>
          <div className="flex items-center gap-3">
            <Icon className="w-7 h-7 text-[#D4A373]" strokeWidth={1.5} />
            <p className="overline !text-[#D4A373]">{hotel.name}</p>
          </div>
          <h1 className="font-serif-x mt-4 text-5xl md:text-7xl tracking-tight leading-[1.05]">
            {pick(service.title, lang)}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-[#FDFBF7]/85 leading-relaxed">
            {pick(service.desc, lang)}
          </p>
          {gallery.length > 1 && (
            <button
              onClick={() => setLightbox({ open: true, startIdx: 0 })}
              className="mt-8 inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase border border-[#FDFBF7]/40 px-5 py-3 hover:bg-[#FDFBF7] hover:text-[#1A1513] transition-colors w-fit"
              data-testid="open-service-gallery"
            >
              <Images className="w-4 h-4" strokeWidth={1.5} />
              {pick({ en: "View gallery", fr: "Voir la galerie", es: "Ver galería" }, lang)} ({gallery.length})
            </button>
          )}
        </div>
      <BrandMonogramEdge size="xl" opacity={0.6} testIdPrefix="page-hero-brand" />
      </section>

      {/* STORY + PRACTICAL */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-8">
            <p className="overline">{pick({ en: "The experience", fr: "L'expérience", es: "La experiencia" }, lang)}</p>
            <h2 className="font-serif-x mt-3 text-4xl md:text-5xl tracking-tight leading-tight text-[#2C2621]">
              {pick(service.title, lang)}
            </h2>
            <p className="mt-8 text-lg leading-relaxed text-[#3C342D] whitespace-pre-line">
              {pick(service.long || service.desc, lang)}
            </p>

            {service.highlights && service.highlights.length > 0 && (
              <div className="mt-12">
                <p className="overline">
                  {pick({ en: "Highlights", fr: "Points forts", es: "Aspectos destacados" }, lang)}
                </p>
                <ul className="mt-5 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {service.highlights.map((h, i) => (
                    <li
                      key={`hl-${h?.en || h || i}`}
                      className="flex gap-3 items-start text-[15px] text-[#3C342D]"
                      data-testid={`service-highlight-${i}`}
                    >
                      <Check className="w-4 h-4 text-[#C16542] mt-1 shrink-0" strokeWidth={1.8} />
                      <span>{pick(h, lang)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Practical side card */}
          <aside className="lg:col-span-4">
            <div className="bg-[#FDFBF7] border border-[#2C2621]/10 p-7 md:p-8 sticky top-28">
              <p className="overline">
                {pick({ en: "Practical", fr: "Pratique", es: "Práctico" }, lang)}
              </p>
              <h3 className="font-serif-x text-2xl mt-3 leading-tight text-[#2C2621]">
                {pick({ en: "Plan your visit", fr: "Planifier votre visite", es: "Planifique su visita" }, lang)}
              </h3>
              <dl className="mt-6 space-y-5 text-sm">
                {service.practical?.hours && (
                  <div className="flex gap-3">
                    <Clock className="w-4 h-4 text-[#C16542] mt-0.5 shrink-0" strokeWidth={1.6} />
                    <div>
                      <dt className="text-[11px] tracking-[0.25em] uppercase text-[#5C5248]">
                        {pick({ en: "Hours", fr: "Horaires", es: "Horario" }, lang)}
                      </dt>
                      <dd className="mt-1 text-[#2C2621]">{pick(service.practical.hours, lang)}</dd>
                    </div>
                  </div>
                )}
                {service.practical?.booking && (
                  <div className="flex gap-3">
                    <Calendar className="w-4 h-4 text-[#C16542] mt-0.5 shrink-0" strokeWidth={1.6} />
                    <div>
                      <dt className="text-[11px] tracking-[0.25em] uppercase text-[#5C5248]">
                        {pick({ en: "Booking", fr: "Réservation", es: "Reserva" }, lang)}
                      </dt>
                      <dd className="mt-1 text-[#2C2621]">{pick(service.practical.booking, lang)}</dd>
                    </div>
                  </div>
                )}
                {service.practical?.from && (
                  <div className="flex gap-3">
                    <Tag className="w-4 h-4 text-[#C16542] mt-0.5 shrink-0" strokeWidth={1.6} />
                    <div>
                      <dt className="text-[11px] tracking-[0.25em] uppercase text-[#5C5248]">
                        {pick({ en: "From", fr: "À partir de", es: "Desde" }, lang)}
                      </dt>
                      <dd className="mt-1 text-[#2C2621]">{pick(service.practical.from, lang)}</dd>
                    </div>
                  </div>
                )}
              </dl>

              <div className="mt-8 pt-6 border-t border-[#2C2621]/10 space-y-3">
                <Link
                  to="/booking"
                  state={{ hotel: hotel.slug, service: serviceSlug }}
                  className="flex items-center justify-between gap-2 bg-[#1A1513] text-[#FDFBF7] px-5 py-3 text-[11px] tracking-[0.25em] uppercase hover:bg-[#C16542] transition-colors"
                  data-testid="service-cta-request"
                >
                  {pick(CTA_LABELS[service.cta] || CTA_LABELS.request, lang)}
                  <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </Link>
                <a
                  href={`tel:${d.contact?.phone || "+212 535 57 84 50"}`}
                  className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542]"
                  data-testid="service-cta-phone"
                >
                  <Phone className="w-3.5 h-3.5" strokeWidth={1.6} />
                  {d.contact?.phone || "+212 535 57 84 50"}
                </a>
                <a
                  href={`mailto:${d.contact?.email || "xaluca@xaluca.com"}`}
                  className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542]"
                  data-testid="service-cta-email"
                >
                  <Mail className="w-3.5 h-3.5" strokeWidth={1.6} />
                  {d.contact?.email || "xaluca@xaluca.com"}
                </a>
                {d.contact?.whatsapp && (
                  <a
                    href={`https://wa.me/${d.contact.whatsapp.replace(/[^\d]/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#5C5248] hover:text-[#C16542]"
                    data-testid="service-cta-whatsapp"
                  >
                    <MessageCircle className="w-3.5 h-3.5" strokeWidth={1.6} />
                    WhatsApp
                  </a>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* RESTAURANTS & BARS — only renders for the bars-restaurants service, when the hotel has fb data */}
      {serviceSlug === "bars-restaurants" && d.fb && (
        <>
          {d.fb.restaurants && d.fb.restaurants.length > 0 && (
            <section className="py-20 md:py-28 bg-[#F2EBE1]/50" data-testid="service-restaurants">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid md:grid-cols-12 gap-6 mb-12">
                  <div className="md:col-span-7">
                    <p className="overline">
                      {pick({ en: "The restaurants", fr: "Les restaurants", es: "Los restaurantes" }, lang)}
                    </p>
                    <h2 className="font-serif-x mt-3 text-4xl md:text-5xl tracking-tight leading-[1.05]">
                      {pick({
                        en: "Four dining rooms, one address.",
                        fr: "Quatre salles à manger, une seule adresse.",
                        es: "Cuatro salones, una sola dirección.",
                      }, lang)}
                    </h2>
                  </div>
                  <div className="md:col-span-5 md:flex md:items-end">
                    <p className="text-base text-[#5C5248] leading-relaxed">
                      {pick({
                        en: "Each of our four restaurants speaks a different culinary register — the canonical Moroccan, the everyday buffet, the chef's tasting menu, the events ballroom.",
                        fr: "Chacun de nos quatre restaurants tient un registre culinaire différent — la cuisine marocaine canonique, le buffet du quotidien, le menu dégustation du chef, le salon des événements.",
                        es: "Cada uno de nuestros cuatro restaurantes habla un registro culinario distinto — la cocina marroquí canónica, el buffet diario, el menú de degustación, el salón de eventos.",
                      }, lang)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                  {d.fb.restaurants.map((r) => {
                    const restaurantSlug = r.name.toLowerCase().replace(/\s+/g, "-");
                    return (
                    <article
                      key={`restaurant-${r.name}`}
                      className="bg-[#FDFBF7] border border-[#2C2621]/10 overflow-hidden flex flex-col"
                      data-testid={`restaurant-card-${restaurantSlug}`}
                      data-cms-scope={`service.${slug}.restaurant.${restaurantSlug}`}
                    >
                      {r.image && (
                        <div className="relative aspect-[16/10] overflow-hidden">
                          <img
                            src={r.image}
                            alt={r.name}
                            className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async"
                            data-cms-role="card.image"/>
                          {r.capacity && (
                            <span className="absolute top-4 left-4 bg-[#1A1513]/85 text-[#FDFBF7] text-[10px] tracking-[0.25em] uppercase px-3 py-1.5">
                              {pick(r.capacity, lang)}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="p-7 md:p-8 flex flex-col flex-1">
                        <p className="overline">
                          {pick(r.style, lang)}
                        </p>
                        <h3 className="font-serif-x text-2xl md:text-3xl mt-3 leading-tight text-[#2C2621]">
                          {r.name}
                        </h3>
                        {r.atmosphere && (
                          <p className="mt-4 text-[15px] text-[#C16542] italic leading-relaxed">
                            {pick(r.atmosphere, lang)}
                          </p>
                        )}
                        <p className="mt-4 text-[15px] text-[#3C342D] leading-relaxed">
                          {pick(r.desc, lang)}
                        </p>
                      </div>
                    </article>
                  );})}
                </div>
              </div>
            </section>
          )}

          {d.fb.bars && d.fb.bars.length > 0 && (
            <section className="py-20 md:py-28 bg-[#1A1513] text-[#FDFBF7]" data-testid="service-bars">
              <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid md:grid-cols-12 gap-6 mb-12">
                  <div className="md:col-span-7">
                    <p className="overline !text-[#D4A373]">
                      {pick({ en: "The bars", fr: "Les bars", es: "Los bares" }, lang)}
                    </p>
                    <h2 className="font-serif-x mt-3 text-4xl md:text-5xl tracking-tight leading-[1.05]">
                      {pick({
                        en: "Two bars, from the cocktail hour onwards.",
                        fr: "Deux bars, à partir de l'apéro.",
                        es: "Dos bares, desde la hora del cóctel.",
                      }, lang)}
                    </h2>
                  </div>
                  <div className="md:col-span-5 md:flex md:items-end">
                    <p className="text-base text-[#FDFBF7]/70 leading-relaxed">
                      {pick({
                        en: "An indoor cocktail bar under a real tataoui ceiling, and an open-air terrace bar at the threshold of the dunes — choose your hour.",
                        fr: "Un bar à cocktails sous un vrai plafond de tataoui, et un bar terrasse à ciel ouvert au seuil des dunes — choisissez votre moment.",
                        es: "Un bar de cócteles bajo un techo auténtico de tataoui, y un bar terraza al aire libre al borde de las dunas — elija su momento.",
                      }, lang)}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
                  {d.fb.bars.map((b) => {
                    const barName = pick(b.name, lang);
                    const stableId = (b.name?.en || barName).toLowerCase().replace(/\s+/g, "-");
                    return (
                      <article
                        key={`bar-${stableId}`}
                        className="bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 overflow-hidden flex flex-col"
                        data-testid={`bar-card-${stableId}`}
                        data-cms-scope={`service.${slug}.bar.${stableId}`}
                      >
                        {b.image && (
                          <div className="relative aspect-[16/10] overflow-hidden">
                            <img
                              src={b.image}
                              alt={barName}
                              className="absolute inset-0 w-full h-full object-cover hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async"
                              data-cms-role="card.image"/>
                          </div>
                        )}
                        <div className="p-7 md:p-8 flex flex-col flex-1">
                          <p className="overline !text-[#D4A373]">
                            {pick(b.style, lang)}
                          </p>
                          <h3 className="font-serif-x text-2xl md:text-3xl mt-3 leading-tight text-[#FDFBF7]">
                            {barName}
                          </h3>
                          {b.atmosphere && (
                            <p className="mt-4 text-[15px] text-[#D4A373] italic leading-relaxed">
                              {pick(b.atmosphere, lang)}
                            </p>
                          )}
                          <p className="mt-4 text-[15px] text-[#FDFBF7]/75 leading-relaxed">
                            {pick(b.desc, lang)}
                          </p>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </>
      )}

      {/* SPA MENU — only renders for the spa-hammam service when the
          hotel exposes a structured `d.spa` catalogue. Same
          hard-content pattern as the bars-restaurants block above:
          the source of truth lives in hotelDetails.js so editors get
          typed, categorised copy without touching JSX. */}
      {serviceSlug === "spa-hammam" && d.spa && (
        <section
          className="py-20 md:py-28 bg-[#F2EBE1]/50"
          data-testid="service-spa-menu"
          data-cms-scope={`service.${slug}.${serviceSlug}.menu`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {/* Intro header */}
            <div className="grid md:grid-cols-12 gap-6 mb-14">
              <div className="md:col-span-7">
                <p className="overline">
                  {pick({ en: "Spa menu", fr: "Carte spa", es: "Carta del spa" }, lang)}
                </p>
                <h2 className="font-serif-x mt-3 text-4xl md:text-5xl tracking-tight leading-[1.05]">
                  {pick({
                    en: "Wellness inspired by Moroccan traditions.",
                    fr: "Un bien-être inspiré des traditions marocaines.",
                    es: "Bienestar inspirado en las tradiciones de Marruecos.",
                  }, lang)}
                </h2>
              </div>
              {d.spa.intro ? (
                <div className="md:col-span-5 md:flex md:items-end">
                  <p className="text-base text-[#5C5248] leading-relaxed">
                    {pick(d.spa.intro, lang)}
                  </p>
                </div>
              ) : null}
            </div>

            {/* Categories */}
            <div className="space-y-16">
              {(d.spa.categories || []).map((cat) => (
                <div
                  key={`spa-cat-${cat.key}`}
                  data-testid={`spa-category-${cat.key}`}
                  data-cms-scope={`service.${slug}.spa.${cat.key}`}
                >
                  <div className="flex items-baseline justify-between gap-4 mb-8">
                    <h3 className="font-serif-x text-2xl md:text-3xl tracking-tight leading-tight">
                      {pick(cat.title, lang)}
                    </h3>
                    <span className="hidden md:block flex-1 border-b border-[#2C2621]/15" aria-hidden="true" />
                    <span className="text-[10px] tracking-[0.28em] uppercase text-[#5C5248] shrink-0">
                      {(cat.items || []).length}
                      {" "}
                      {pick({
                        en: (cat.items || []).length === 1 ? "treatment" : "treatments",
                        fr: (cat.items || []).length === 1 ? "soin" : "soins",
                        es: (cat.items || []).length === 1 ? "tratamiento" : "tratamientos",
                      }, lang)}
                    </span>
                  </div>

                  {cat.layout === "grid" ? (
                    // Compact 2-column tariff card (hair-removal)
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {(cat.items || []).map((it) => (
                        <div
                          key={`spa-grid-${cat.key}-${it.key}`}
                          className="flex items-center justify-between gap-4 px-5 py-4 bg-[#FDFBF7] border border-[#2C2621]/10"
                          data-testid={`spa-item-${cat.key}-${it.key}`}
                        >
                          <span className="text-sm text-[#2C2621]">
                            {pick(it.name, lang)}
                          </span>
                          <span className="font-serif-x text-lg text-[#C16542] tabular-nums shrink-0">
                            {it.price}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Full-body treatment cards (hammam / sauna / massage / experience)
                    <div className={`grid grid-cols-1 ${cat.items.length > 1 ? "md:grid-cols-2" : ""} gap-6`}>
                      {(cat.items || []).map((it) => {
                        const featured = it.featured === true;
                        return (
                          <article
                            key={`spa-item-${cat.key}-${it.key}`}
                            className={`flex flex-col p-6 md:p-7 ${featured ? "bg-[#1A1513] text-[#FDFBF7] border border-[#C16542]" : "bg-[#FDFBF7] border border-[#2C2621]/10"}`}
                            data-testid={`spa-item-${cat.key}-${it.key}`}
                            data-cms-scope={`service.${slug}.spa.${cat.key}.${it.key}`}
                          >
                            {featured ? (
                              <p className="overline !text-[#D4A373] mb-2">
                                {pick({ en: "Signature circuit", fr: "Circuit signature", es: "Circuito insignia" }, lang)}
                              </p>
                            ) : null}

                            <h4 className={`font-serif-x text-xl md:text-2xl tracking-tight leading-tight ${featured ? "text-[#FDFBF7]" : "text-[#2C2621]"}`}>
                              {pick(it.name, lang)}
                            </h4>

                            {/* Price / duration row */}
                            {it.variants && it.variants.length > 0 ? (
                              <ul className="mt-4 space-y-1.5">
                                {it.variants.map((v) => (
                                  <li
                                    key={`spa-variant-${cat.key}-${it.key}-${v.duration}`}
                                    className={`flex items-baseline justify-between gap-4 text-sm ${featured ? "text-[#FDFBF7]/90" : "text-[#2C2621]"}`}
                                  >
                                    <span className="tracking-[0.05em]">{v.duration}</span>
                                    <span className={`font-serif-x text-lg tabular-nums ${featured ? "text-[#D4A373]" : "text-[#C16542]"}`}>{v.price}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <div className={`mt-4 flex items-baseline gap-3 ${featured ? "text-[#FDFBF7]/90" : "text-[#2C2621]"}`}>
                                <span className={`font-serif-x text-2xl md:text-3xl tabular-nums ${featured ? "text-[#D4A373]" : "text-[#C16542]"}`}>{it.price}</span>
                                {it.duration ? (
                                  <span className="text-[10px] tracking-[0.28em] uppercase opacity-70">
                                    {pick(it.duration, lang)}
                                  </span>
                                ) : null}
                              </div>
                            )}

                            {it.desc ? (
                              <p className={`mt-5 text-sm md:text-[15px] leading-relaxed ${featured ? "text-[#FDFBF7]/85" : "text-[#5C5248]"}`}>
                                {pick(it.desc, lang)}
                              </p>
                            ) : null}

                            {it.includes && it.includes.length > 0 ? (
                              <ul className="mt-5 space-y-2 border-t pt-5 border-[#FDFBF7]/20">
                                {it.includes.map((inc, i) => (
                                  <li
                                    key={`spa-inc-${cat.key}-${it.key}-${i}`}
                                    className={`flex items-start gap-3 text-sm ${featured ? "text-[#FDFBF7]/90" : "text-[#2C2621]"}`}
                                  >
                                    <span className={`mt-2 w-1 h-1 shrink-0 ${featured ? "bg-[#D4A373]" : "bg-[#C16542]"}`} aria-hidden="true" />
                                    <span>{pick(inc, lang)}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : null}
                          </article>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Notes */}
            {d.spa.notes && d.spa.notes.length > 0 ? (
              <div className="mt-16 border-t border-[#2C2621]/15 pt-8" data-testid="spa-notes">
                <p className="overline mb-4">
                  {pick({ en: "Good to know", fr: "Bon à savoir", es: "Información" }, lang)}
                </p>
                <ul className="space-y-2 text-sm text-[#5C5248] leading-relaxed">
                  {d.spa.notes.map((note, i) => (
                    <li key={`spa-note-${i}`} className="flex items-start gap-3">
                      <span className="mt-2 w-1 h-1 shrink-0 bg-[#C16542]" aria-hidden="true" />
                      <span>{pick(note, lang)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </section>
      )}

      {/* GALLERY STRIP */}
      {gallery.length > 1 && (
        <section className="pb-20 md:pb-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="overline">
              {pick({ en: "Gallery", fr: "Galerie", es: "Galería" }, lang)}
            </p>
            <h2 className="font-serif-x mt-3 text-3xl md:text-4xl tracking-tight leading-tight">
              {pick(service.title, lang)}
            </h2>
            <div
              className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3"
              data-cms-scope={`service.${slug}.${serviceSlug}.gallery`}
              // Opt every child <img> out of AutoImageEditor so slot-storage
              // overrides can never desync the grid from the array we pass
              // to the Lightbox. Gallery content is edited through the
              // canonical `/admin/galleries` route only.
              data-no-cms-edit="true"
              data-testid="service-gallery-grid"
            >
              {gallery.slice(0, 8).map((src, i) => (
                <button
                  key={`gallery-${i}-${src}`}
                  onClick={() => setLightbox({ open: true, startIdx: i })}
                  className="aspect-[4/5] overflow-hidden group"
                  data-testid={`service-gallery-thumb-${i}`}
                  aria-label={`${pick(service.title, lang)} — ${pick({ en: "Open image", fr: "Ouvrir l'image", es: "Abrir imagen" }, lang)} ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`${pick(service.title, lang)} ${i + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* OTHER SERVICES */}
      {otherServices.length > 0 && (
        <section className="py-20 md:py-24 bg-[#F2EBE1]/40">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-end justify-between mb-10">
              <div>
                <p className="overline">
                  {pick({ en: "Discover more", fr: "Découvrir plus", es: "Descubrir más" }, lang)}
                </p>
                <h2 className="font-serif-x mt-3 text-3xl md:text-4xl tracking-tight leading-tight">
                  {pick({ en: "Other services at the kasbah", fr: "Autres services de la kasbah", es: "Otros servicios de la kasbah" }, lang)}
                </h2>
              </div>
              <Link
                to={`/hotels/${slug}`}
                className="hidden md:inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase border-b border-[#2C2621]/40 pb-1 hover:border-[#C16542] hover:text-[#C16542] transition-colors"
                data-testid="back-to-hotel-link"
              >
                {pick({ en: "Back to hotel", fr: "Retour à l'hôtel", es: "Volver al hotel" }, lang)}
                <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {otherServices.map((s, i) => {
                const OIcon = ICONS[s.icon] || Sparkles;
                return (
                  <Link
                    key={`other-${s.slug || s.title?.en || i}`}
                    to={`/hotels/${slug}/services/${s.slug}`}
                    className="group bg-[#FDFBF7] border border-[#2C2621]/10 p-6 hover:border-[#C16542]/40 transition-colors block"
                    data-testid={`other-service-${i}`}
                  >
                    <OIcon className="w-6 h-6 text-[#C16542]" strokeWidth={1.5} />
                    <h3 className="font-serif-x text-xl mt-4 leading-tight">
                      {pick(s.title, lang)}
                    </h3>
                    <p className="mt-2 text-sm text-[#5C5248] leading-relaxed line-clamp-3">
                      {pick(s.desc, lang)}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase border-b border-[#2C2621]/30 pb-0.5 w-fit group-hover:border-[#C16542] group-hover:text-[#C16542] transition-colors">
                      {pick({ en: "View", fr: "Voir", es: "Ver" }, lang)}
                      <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {lightbox.open && (
        <Lightbox
          images={gallery}
          title={pick(service.title, lang)}
          startIdx={lightbox.startIdx}
          onClose={() => setLightbox({ open: false, startIdx: 0 })}
        />
      )}
    </div>
  );
};

export default ServiceDetailPage;
